import React from "react";
import { BarChart, Bar, XAxis, ResponsiveContainer, LabelList, Cell } from "recharts";
import * as m from "../lib/model";
import { parseMoney, parsePct, years, fmtBig, fmtTight, grow, filled, defaultLevers } from "./room/deck";
import { qsbsEconomics, qsbsBump } from "./room/Qsbs";
import { precedentRows } from "./room/Precedent";

// 16:9 investor pitchbook. Every figure is read from the live model; headlines and body copy can be
// typed over per slide in the CIM Builder. Light stock: ivory paper, navy ink, one lime accent.
export const PB_SLIDES = [
  { id: "cover", label: "Cover", desc: "Title, the ask, cover photo" },
  { id: "toc", label: "Table of contents", desc: "Auto-numbered from the slides you include" },
  { id: "summary", label: "Executive summary", desc: "The opportunity + the LP advantage" },
  { id: "market", label: "Market opportunity", desc: "TAM, CAGR, why consolidation now" },
  { id: "platform", label: "The platform", desc: "Profile table + why it is the right platform" },
  { id: "strategy", label: "Acquisition strategy", desc: "Target profile, deployment, value levers" },
  { id: "valuation", label: "Valuation landscape", desc: "Entry vs exit multiple arbitrage" },
  { id: "precedents", label: "Precedent transactions", desc: "Comparable deals + the so-what" },
  { id: "structure", label: "Structure & use of proceeds", desc: "Vehicle terms + use of equity" },
  { id: "qsbs", label: "QSBS advantage — §1202", desc: "Exclusion caps, holding period, the plan" },
  { id: "qsbslp", label: "QSBS — LP economics", desc: "With / without §1202 on a sample check" },
  { id: "refi", label: "Refinance strategy", desc: "Fund lifecycle timeline" },
  { id: "backlever", label: "Back-lever scenario", desc: "Firepower comparison (needs back-lever on)" },
  { id: "scenarios", label: "Pro forma — return scenarios", desc: "Downside / Base / Upside matrix" },
  { id: "risks", label: "Risks & mitigants", desc: "The real risks and how structure answers them" },
  { id: "ask", label: "The ask", desc: "Reserve amount, target return, next steps" },
];

const pctS = (n) => (isNaN(n) ? "n/a" : `${(n || 0).toFixed(1)}%`);
const Ask = ({ label, value, sub }) => (
  <div className="pb-ask"><span>{label}</span><b>{value}</b>{sub ? <i>{sub}</i> : null}</div>
);
const Rows = ({ rows, testid }) => (
  <div className="pb-rows" data-testid={testid}>
    {rows.filter((r) => r && r[1] != null && r[1] !== "").map(([k, v]) => (
      <React.Fragment key={k}><span>{k}</span><b>{v}</b></React.Fragment>
    ))}
  </div>
);
const Cards = ({ items, testid }) => (
  <div className="pb-cards" data-testid={testid}>
    {items.slice(0, 4).map((c, i) => <div key={i}><b>{c.title}</b><span>{c.text}</span></div>)}
  </div>
);

function Slide({ id, n, of, title, eyebrow, headline, body, children, firmName, dealLabel }) {
  return (
    <section className="pb-slide" data-pb-slide data-testid={`pb-slide-${id}`}>
      <header className="pb-head">
        <h2>{title}</h2>
        {eyebrow ? <span>{eyebrow}</span> : null}
      </header>
      {headline ? <div className="pb-lead">{headline}</div> : null}
      {body ? <p className="pb-body">{body}</p> : null}
      <div className="pb-canvas">{children}</div>
      <footer className="pb-foot"><span>{firmName} · Confidential</span><span>{dealLabel}</span><span>{n} / {of}</span></footer>
    </section>
  );
}

export const Pitchbook = React.forwardRef(function Pitchbook({ M, market, share, firm, cover = [], ov = {}, secs = {}, title }, ref) {
  const MK = market || {};
  const firmName = firm?.name || "XYZ Ventures LLC";
  const thesis = (share && share.thesis) || {};
  const label = title || M.dealName;
  const ex = m.exitReturns(M), wf = m.lpWaterfall(M), cov = m.coverage(M), plan = m.cashPlan(M);
  const ask = m.lpAskOf(M), consolidated = m.hasRollup(M);
  const rh = consolidated ? m.rollupHeadline(M) : null;
  const addons = (rh && Array.isArray(rh.rows) ? rh.rows : []).filter((r) => r.year > 0).length;
  const roll = consolidated && addons > 0;
  const entry = m.entryMultiple(M), exitMult = m.coerceNum(M.exitMult);
  const bump = qsbsBump(M);
  const hold = m.effectiveHold(M);
  const bl = m.backLever(M);
  const su = m.sourcesUses(M);
  const H = (id, fallback) => (ov[id]?.h || "").trim() || fallback;
  const B = (id, fallback) => (ov[id]?.b || "").trim() || fallback;

  const live = PB_SLIDES.filter((s) => secs[s.id] && (s.id !== "backlever" || (bl && bl.active)));
  const numbered = live.filter((s) => s.id !== "cover");
  const of = live.length;
  const nOf = (id) => live.findIndex((s) => s.id === id) + 1;
  const S = (id, props) => (
    <Slide key={id} id={id} n={nOf(id)} of={of} firmName={firmName} dealLabel={label} {...props} />
  );

  // ---- market ----
  const tam = MK.tam || {};
  const mk = thesis.marketBlock || {};
  const size = +mk.size || +tam.tamNum || parseMoney(tam.value || tam.tam || tam.total);
  const cagr = +mk.cagr || parsePct(tam.cagr);
  const ys = years(tam.value), cy = years(tam.cagr);
  const baseYear = +mk.baseYear || ys[0] || cy[0] || new Date().getFullYear();
  const endYear = +mk.endYear || (cy.length && cy[cy.length - 1] > baseYear ? cy[cy.length - 1] : baseYear + 5);
  const span = Math.max(1, endYear - baseYear);
  const forecast = +mk.forecast || grow(size, cagr, span);
  const unit = size >= 1e9 ? 1e9 : 1e6, uLbl = size >= 1e9 ? "$B" : "$M";
  const mkData = [baseYear, endYear].map((y) => ({ year: y, v: +(grow(size, cagr, y - baseYear) / unit).toFixed(1) }));
  const tails = (Array.isArray(MK.tailwinds) ? MK.tailwinds : []).map((t) => (typeof t === "string" ? t : t?.text || t?.name || "")).filter(Boolean);
  const cons = (Array.isArray(MK.consolidators) ? MK.consolidators : []).map((c) => c.name || c).filter(Boolean).slice(0, 3);
  const drivers = filled(mk.drivers, "title").length ? filled(mk.drivers, "title") : [
    { title: "Aging owners", text: "A large cohort of founder-owned operators is reaching retirement without family succession." },
    { title: "Fragmented supply", text: tails[0] || "Thousands of single-location operators; national share is de minimis and the off-market pipeline is deep." },
    { title: "Recurring economics", text: tails[1] || "Service and repeat revenue carry higher margin and stay durable through cycles." },
    { title: "Active precedents", text: cons.length ? `${cons.join(", ")} are actively consolidating the category.` : "PE-backed consolidators are actively buying in the category." },
  ];

  // ---- platform ----
  const own = (thesis.thePlatform || {}).own || {};
  const platEbitda = +own.ebitda || M.sde || 0;
  const gm = m.grossMargin(M);
  const platRows = [
    ["Location", M.address],
    ["TTM revenue", (+own.revenue || M.ttmRevenue) ? m.fmt$(+own.revenue || M.ttmRevenue) : null],
    ["Platform EBITDA", platEbitda ? `${m.fmt$(platEbitda)} (adjusted)` : null],
    ["Gross margin", gm ? m.fmtPct(gm) : null],
    ["Employees", M.employeeCount || null],
    ["Established", M.yearEstablished || null],
    ["Sponsor ownership", own.ownershipPct != null || M.rollPct > 0
      ? `${Math.round(own.ownershipPct ?? (100 - (M.rollPct || 0)))}% owned${M.rollPct > 0 ? ` · seller rolls ${M.rollPct}% (${m.fmt$(m.rolloverAmt(M))})` : ""}` : null],
  ];
  const whyPlat = filled((thesis.thePlatform || {}).why, "title").length ? filled((thesis.thePlatform || {}).why, "title") : [
    { title: "Cash coverage", text: `Year-${cov.year} debt service is covered ${m.fmtX(cov.dscr)} on ${m.fmt$(platEbitda)} of adjusted earnings — distributions do not depend on growth.` },
    { title: "Operating muscle", text: `${M.employeeCount || "An"} ${M.employeeCount ? "employees and an" : ""} established ${(M.industry || "operating").toLowerCase()} platform with the systems and staff to absorb bolt-ons.` },
    { title: "Geographic fit", text: `${M.address ? M.address.split(",").pop().trim() : "The region"} is the natural hub for add-ons in the corridor.` },
    { title: "Owner alignment", text: M.rollPct > 0 ? `The seller rolls ${M.rollPct}% (${m.fmt$(m.rolloverAmt(M))}) alongside LP investors — continuity for customers and staff.` : "Seller stays through transition to protect customers, staff and supplier relationships." },
  ];

  // ---- strategy ----
  const targets = filled((thesis.acq || {}).targets, "value").length ? filled((thesis.acq || {}).targets, "value").map((r) => [r.label, r.value]) : [
    ["Target EBITDA", rh ? `${m.fmt$((rh.totEbitda - (M.sde || 0)) / Math.max(1, rh.rows?.filter((r) => r.year > 0).length || 1))} per target` : `${m.fmt$(M.sde || 0)} per target`],
    ["Geography", `${M.address ? M.address.split(",").pop().trim() : "In-corridor"} priority — same corridor as the platform`],
    ["Entry multiple", `${m.fmtX(entry)} LTM EBITDA (SDE-adjusted)`],
    ["Service overlap", "Same trade and customer set — shared crews, parts and back office"],
    ["Owner situation", "Retiring owners with no succession preferred"],
  ];
  const levers = Array.isArray(thesis.levers) && thesis.levers.length ? thesis.levers : defaultLevers(M);

  // ---- structure ----
  const vehicle = [
    ["Vehicle", thesis.qsbsEligible !== false ? "Domestic C-corporation (QSBS-eligible)" : "Domestic LLC"],
    ["Fund size", `${m.fmt$(ask)} LP equity`],
    ["Instrument", `Preferred equity — ${M.prefReturn}% cumulative pref`],
    ["Sponsor", firmName],
    ["LP / GP split", `${M.lpSplit} / ${100 - M.lpSplit} after pref and return of capital`],
    ["Management fee", M.mgmtFeePct > 0 ? `${M.mgmtFeePct}% of ${M.mgmtFeeBasis || "EBITDA"}` : "None"],
    ["Hold period", M.holdIndefinite ? "Indefinite — cash-flow hold" : `${M.hold} years`],
    ["Target return", `${m.fmtX(wf.lpMoic)} MOIC / ${pctS(wf.lpIrr)} LP IRR${bump ? ` · +${m.fmtX(bump.x)} QSBS after tax` : ""}`],
    ["Financing at close", m.loanAmt(M) > 0 ? `${m.fmt$(m.loanAmt(M))} senior @ ${M.rate}% / ${M.loanYrs} yrs` : "All-equity at close"],
    ["Distributions", m.isRetain(M) ? `${Math.round(m.payoutPctOf(M))}% of free cash distributed — ${100 - Math.round(m.payoutPctOf(M))}% retained for add-ons` : "100% of free cash distributed as generated"],
  ];
  const uses = (su.uses || []).map((u) => (Array.isArray(u) ? { d: u[0], a: u[1] } : u)).filter((u) => u.a > 0.5);
  const totalUses = su.totalUses || uses.reduce((a, u) => a + u.a, 0);

  // ---- qsbs ----
  const q = qsbsEconomics(M, 100000);

  // ---- scenarios ----
  const scenRow = (scen) => {
    const SM = m.scenarioModel(M, scen);
    const w = m.lpWaterfall(SM), e = m.exitReturns(SM), c = m.coverage(SM);
    return { lp: m.lpAskOf(SM), debt: m.loanAmt(SM), ebitda: m.proForma(SM).sde || SM.sde, entry: m.entryMultiple(SM),
      exitMult: m.coerceNum(SM.exitMult), ev: e.exitEV, dscr: c.dscr, moic: w.lpMoic, irr: w.lpIrr };
  };
  const scens = [["Downside", scenRow("downside")], ["Base", scenRow("base")], ["Upside", scenRow("upside")]];

  // ---- risks ----
  const tr = m.revenueTrend(M);
  const risks = filled(thesis.risks, "title").length ? filled(thesis.risks, "title") : [
    { title: "Owner dependency", text: "Key-person risk through transition — seller retained under a transition agreement, crew and customer contacts documented in diligence." },
    { title: "Debt service coverage", text: `Year-${cov.year} DSCR of ${m.fmtX(cov.dscr)}${cov.dscr < 1.3 ? " sits below the 1.30× comfort threshold — structure holds a cash reserve" : " clears the 1.30× comfort threshold with room for a revenue shock"}.` },
    { title: "Earnings quality", text: `True operating earnings after a market salary are ≈ ${m.fmt$(m.trueEarnings(M))}; the model underwrites that number, not headline SDE.` },
    { title: "Revenue trend", text: tr.declinePct > 5 ? `Revenue declined ${tr.declinePct.toFixed(0)}% across the review period — diligence must explain the driver before close.` : "Top line is stable across the review period; diligence confirms mix and customer concentration." },
    { title: "Customer concentration", text: `Diligence must confirm no single customer dominates the ${m.fmt$(M.ttmRevenue)} top line.` },
    { title: "Integration execution", text: "Add-ons close sequentially, not in parallel; sellers are retained as regional managers so talent and culture travel with the deal." },
  ];

  const content = {
    cover: () => (
      <section className="pb-slide pb-cover" data-pb-slide data-testid="pb-slide-cover" key="cover">
        {cover.length > 0 && (
          <div className="pb-cover-art" data-testid="pb-cover-art">
            {cover.slice(0, 3).map((src, i) => <img key={src + i} src={src} alt="" style={{ animationDelay: `${i * 6}s` }} />)}
          </div>
        )}
        <div className="pb-cover-ink">
          <span className="pb-eyebrow">{firmName} · Confidential investment memorandum</span>
          <h1>{label}</h1>
          <div className="pb-cover-sub">{H("cover", [M.industry, M.address, roll ? `${addons} add-ons pro-forma` : null].filter(Boolean).join(" · "))}</div>
          <div className="pb-asks">
            <Ask label="The ask" value={m.fmt$(ask)} sub="LP equity" />
            <Ask label="LP MOIC" value={m.fmtX(wf.lpMoic)} sub="base case, before tax" />
            <Ask label="LP IRR" value={pctS(wf.lpIrr)} sub={bump ? `+${bump.bps} bps with QSBS` : "gross of tax"} />
            <Ask label={`DSCR Y${cov.year}`} value={m.fmtX(cov.dscr)} sub={cov.pf ? "fully deployed" : "at close"} />
          </div>
          <div className="pb-cover-foot">{new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} · Prepared for the named recipient · Illustrative projections; not an offer to sell securities</div>
        </div>
      </section>
    ),
    toc: () => S("toc", {
      title: "Contents", eyebrow: `${numbered.length} sections`,
      children: (
        <ol className="pb-toc" data-testid="pb-toc">
          {numbered.map((s) => <li key={s.id}><span className="pb-toc-n">{String(nOf(s.id)).padStart(2, "0")}</span><b>{s.label}</b><i>{s.desc}</i></li>)}
        </ol>
      ),
    }),
    summary: () => S("summary", {
      title: "Executive summary",
      headline: H("summary", `${m.fmt$(ask)} LP vehicle${roll ? ` to consolidate ${addons} operators into a ${m.fmt$(rh?.totEbitda || 0)}-EBITDA platform` : ` to acquire ${M.dealName} at ${m.fmtX(entry)} adjusted earnings`}`),
      children: (
        <div className="pb-2col">
          <div>
            <Rows testid="pb-sum-rows" rows={[
              ["LP equity", m.fmt$(ask)],
              ["Senior debt at close", m.loanAmt(M) > 0 ? m.fmt$(m.loanAmt(M)) : "None"],
              roll ? ["Add-ons", `${addons} total`] : ["Purchase price", m.fmt$(M.askPrice)],
              ["Entry multiple", m.fmtX(entry)],
              ["Targeted exit multiple", m.fmtX(exitMult)],
              ["Hold", M.holdIndefinite ? "Indefinite" : `${M.hold} years`],
            ]} />
          </div>
          <div className="pb-prose">
            <h3>The opportunity</h3>
            <p>{B("summary", `${M.dealName} generates ${m.fmt$(M.ttmRevenue)} of revenue and ${m.fmt$(M.sde)} of adjusted EBITDA${M.employeeCount ? ` with ${M.employeeCount} employees` : ""}${roll ? `, growing to ${m.fmt$(m.proForma(M).revenue || 0)} revenue and ${m.fmt$(rh?.totEbitda || 0)} EBITDA once every add-on is closed` : ""}. The acquisition is funded with ${m.fmt$(m.loanAmt(M))} senior debt and ${m.fmt$(ask)} of LP equity. Year-${cov.year} cash flow covers debt service ${m.fmtX(cov.dscr)}${cov.pf ? " once fully deployed" : ""}. The base case returns ${m.fmtX(wf.lpMoic)} / ${pctS(wf.lpIrr)} to LPs after ${/^8/.test(String(M.prefReturn)) ? "an" : "a"} ${M.prefReturn}% preferred return.`)}</p>
            <h3>The LP advantage</h3>
            <p>LPs sit senior to the sponsor: a {M.prefReturn}% cumulative preferred return accrues before the GP participates, capital is returned in full before any profit split, and the GP is paid only on performance ({100 - M.lpSplit}% of profits after pref and return of capital{M.mgmtFeePct > 0 ? `, ${M.mgmtFeePct}% management fee` : ""}). {thesis.qsbsEligible !== false ? `Stock is structured to qualify for QSBS §1202 treatment — federal long-term gain excluded up to the greater of $15,000,000 or 10× basis after a five-year hold.` : ""}</p>
          </div>
        </div>
      ),
    }),
    market: () => S("market", {
      title: "Market opportunity",
      headline: H("market", size ? `A ${fmtBig(size)} market growing ${cagr.toFixed(1)}% annually — ${(MK.sector || M.industry || "the category").toLowerCase()} ripe for consolidation` : "Market sizing pending — generate the market report on AI Evaluation"),
      children: (
        <div className="pb-2col">
          <div>
            <Rows testid="pb-mkt-rows" rows={[
              [`${baseYear} market size`, size ? fmtBig(size) : "—"],
              [`${endYear} forecast`, forecast ? fmtBig(forecast) : "—"],
              ["CAGR", cagr ? `${cagr.toFixed(1)}% ${baseYear}–${endYear}` : "—"],
            ]} />
            {size > 0 && (
              <div className="pb-chart" data-testid="pb-mkt-chart">
                <div className="pb-chart-cap">Market size — {uLbl}</div>
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={mkData} margin={{ top: 18, right: 8, left: 8, bottom: 0 }}>
                    <XAxis dataKey="year" tick={{ fontSize: 10, fill: "#4a5568" }} axisLine={false} tickLine={false} />
                    <Bar dataKey="v" radius={[2, 2, 0, 0]} isAnimationActive={false} barSize={54}>
                      {mkData.map((d, i) => <Cell key={i} fill={i === mkData.length - 1 ? "#c6f24e" : "#152238"} />)}
                      <LabelList dataKey="v" position="top" style={{ fontSize: 11, fill: "#152238", fontWeight: 700 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {(mk.sources || tam.note) && <div className="pb-fine">Sources: {mk.sources || tam.note}</div>}
          </div>
          <div>
            <h3 className="pb-h3">Why consolidation now</h3>
            <Cards items={drivers} testid="pb-mkt-drivers" />
          </div>
        </div>
      ),
    }),
    platform: () => S("platform", {
      title: consolidated ? "The platform" : "The business",
      headline: H("platform", (thesis.thePlatform || {}).headline || `${M.dealName} — an established ${(M.industry || "operating").toLowerCase()} ${consolidated ? "platform" : "business"}${M.yearEstablished ? ` (est. ${M.yearEstablished})` : ""}${platEbitda ? ` with ${m.fmt$(platEbitda)} of adjusted EBITDA` : ""}`),
      children: (
        <div className="pb-2col">
          <div><h3 className="pb-h3">Profile</h3><Rows rows={platRows} testid="pb-plat-rows" /></div>
          <div><h3 className="pb-h3">Why this is the right {consolidated ? "platform" : "acquisition"}</h3><Cards items={whyPlat} testid="pb-plat-why" /></div>
        </div>
      ),
    }),
    strategy: () => S("strategy", {
      title: "Acquisition strategy",
      headline: H("strategy", (thesis.acq || {}).headline || (roll
        ? `Buy ${addons} lower-middle-market operators at ${m.fmtX(rh?.blendedEntry || entry)} blended — build a ${m.fmt$(rh?.totEbitda || 0)} EBITDA platform`
        : `Acquire at ${m.fmtX(entry)} and grow adjusted earnings ${M.sdeGrowth}% a year toward a ${m.fmtX(exitMult)} exit`)),
      children: (
        <div className="pb-2col">
          <div><h3 className="pb-h3">Target profile</h3><Rows rows={targets} testid="pb-strat-targets" />
            <div className="pb-fine">{B("strategy", roll
              ? `Illustrative deployment of ${m.fmt$(rh?.totPrice || 0)} across the add-on schedule. Actual mix varies with target LTM EBITDA and multiples negotiated.`
              : `Funded with ${m.fmt$(m.loanAmt(M))} senior debt and ${m.fmt$(ask)} LP equity at close.`)}</div>
          </div>
          <div><h3 className="pb-h3">Value creation levers</h3>
            <ol className="pb-levers" data-testid="pb-strat-levers">
              {levers.slice(0, 4).map((l, i) => <li key={i}><b>{l.title}</b><span>{l.text}</span></li>)}
            </ol>
          </div>
        </div>
      ),
    }),
    valuation: () => S("valuation", {
      title: "Valuation landscape",
      headline: H("valuation", `The multiple-arbitrage math: buy at ${m.fmtX(entry)}, exit at ${m.fmtX(exitMult)} EV / EBITDA`),
      children: (
        <div className="pb-2col">
          <div className="pb-arb" data-testid="pb-arb">
            <span>The arbitrage</span>
            <b>{m.fmtX(Math.max(0, exitMult - entry))}</b>
            <i>turns of multiple lift between entry and exit</i>
          </div>
          <div className="pb-prose">
            <p>{B("valuation", roll
              ? `An average add-on of ${m.fmt$((rh?.totEbitda - (M.sde || 0)) / Math.max(1, addons))} of EBITDA bought at ${m.fmtX(rh?.blendedEntry || entry)} costs roughly ${m.fmt$(((rh?.totEbitda - (M.sde || 0)) / Math.max(1, addons)) * (rh?.blendedEntry || entry))}. Rolled into a ${m.fmt$(rh?.totEbitda || 0)}-EBITDA platform valued at ${m.fmtX(exitMult)}, the same dollar of EBITDA is worth ${m.fmtX(exitMult)} at exit — every incremental $1 of add-on EBITDA is worth about $${Math.max(0, exitMult - (rh?.blendedEntry || entry)).toFixed(2)} more than it cost.`
              : `Entry at ${m.fmtX(entry)} on ${m.fmt$(M.sde)} of adjusted earnings against a ${m.fmtX(exitMult)} exit on ${m.fmt$(ex.sdeExit)} of exit earnings creates ${m.fmt$(Math.max(0, ex.exitEV - m.nac(M)))} of enterprise value before any debt paydown.`)}</p>
            <Rows rows={[
              ["Entry multiple", m.fmtX(entry)],
              ["Exit multiple", m.fmtX(exitMult)],
              ["Exit enterprise value", M.holdIndefinite ? "Cash-flow hold" : m.fmt$(ex.exitEV)],
              ["Debt retired by exit", m.fmt$(Math.max(0, (m.loanAmt(M) + (M.snAmt || 0)) - ex.loanBal))],
              ["Equity at exit", m.fmt$(ex.equityAtExit)],
            ]} testid="pb-val-rows" />
          </div>
        </div>
      ),
    }),
    precedents: () => {
      const rows = precedentRows(MK, (thesis.precedent || {}).rows);
      return S("precedents", {
        title: "Precedent transactions",
        headline: H("precedents", (thesis.precedent || {}).headline || `The playbook is being run — successfully${M.address ? ` — in ${M.address.split(",").pop().trim()}` : ""}`),
        children: rows.length ? (
          <>
            <table className="pb-tbl" data-testid="pb-prec-tbl">
              <thead><tr><th>Date</th><th>Buyer</th><th>Target</th><th>Geography</th><th>Strategic read</th></tr></thead>
              <tbody>{rows.slice(0, 6).map((r, i) => (
                <tr key={i}><td>{r.date}</td><td><b>{r.buyer}</b></td><td>{r.target}</td><td>{r.geography}</td><td>{r.read}</td></tr>
              ))}</tbody>
            </table>
            <div className="pb-sowhat" data-testid="pb-prec-sowhat"><span>So what</span><p>{B("precedents", (thesis.precedent || {}).soWhat || `Institutional consolidators already pay platform multiples for the same assets ${firmName} buys at ${m.fmtX(entry)} — and founder-operator credibility with seller counterparts is an edge those buyers cannot easily match.`)}</p></div>
          </>
        ) : <div className="pb-empty">No comparable transactions yet — generate the market report on AI Evaluation and they fill in here.</div>,
      });
    },
    structure: () => S("structure", {
      title: "Structure & use of proceeds",
      headline: H("structure", (thesis.struct || {}).headline || `${m.fmt$(ask)} LP equity${m.loanAmt(M) > 0 ? ` + ${m.fmt$(m.loanAmt(M))} senior debt` : ""}${thesis.qsbsEligible !== false ? " — C-corp structured for §1202 QSBS eligibility" : ""}`),
      children: (
        <div className="pb-2col">
          <div><h3 className="pb-h3">Vehicle terms</h3><Rows rows={vehicle} testid="pb-struct-terms" /></div>
          <div><h3 className="pb-h3">Use of proceeds ({m.fmt$(totalUses)})</h3>
            <table className="pb-tbl tight" data-testid="pb-struct-uses">
              <tbody>
                {uses.slice(0, 9).map((u) => (
                  <tr key={u.d}><td>{u.d}</td><td className="num">{fmtTight(u.a)}</td><td className="num pb-pct">{totalUses ? `${Math.round((u.a / totalUses) * 100)}%` : "—"}</td></tr>
                ))}
                <tr className="tot"><td>Total uses</td><td className="num">{fmtTight(totalUses)}</td><td className="num">100%</td></tr>
              </tbody>
            </table>
            <div className="pb-sowhat"><span>Why this capital structure</span><p>{B("structure", `Equity is sized so the preferred return is covered from operating cash, not exit proceeds — Year-${cov.year} coverage is ${m.fmtX(cov.dscr)}.`)}</p></div>
          </div>
        </div>
      ),
    }),
    qsbs: () => S("qsbs", {
      title: "QSBS advantage — §1202",
      headline: H("qsbs", "The 2025 OBBBA expansion makes this one of the most tax-efficient LP structures available"),
      children: (
        <div className="pb-2col">
          <div className="pb-stats" data-testid="pb-qsbs-stats">
            <div><b>$15M</b><span>Per-taxpayer exclusion cap — or 10× basis if greater, for stock issued after 4 July 2025</span></div>
            <div><b>100%</b><span>Federal capital-gains exclusion after a five-year hold</span></div>
            <div><b>$50M</b><span>{`Corporate gross-asset ceiling at issuance — a ${m.fmt$(ask)} vehicle fits comfortably`}</span></div>
            <div><b>0%</b><span>Federal capital-gains tax on qualifying gain, against 23.8% at top LTCG + NIIT</span></div>
          </div>
          <div className="pb-prose">
            <h3>Our plan</h3>
            <ul className="pb-list">
              <li>Hold every LP position at least five years — the {hold}-year model clears the {hold >= 5 ? "100%" : "partial"} exclusion tier.</li>
              <li>{bl && bl.active ? `Refinance senior debt in Year ${bl.year} to distribute cash while preserving the QSBS clock — shares are never redeemed.` : "Distribute from operating cash rather than redeeming shares, so the QSBS clock is never broken."}</li>
              <li>{M.holdIndefinite ? "Hold indefinitely; any future exit is structured as a stock sale." : `Exit in Year ${M.hold} at the platform multiple as a stock sale.`}</li>
              <li>Gain up to the greater of $15,000,000 or 10× basis is federally excluded — per taxpayer, per issuer.</li>
            </ul>
            <div className="pb-fine">{B("qsbs", "Not tax advice. §1202 qualification depends on corporate-level tests being satisfied at issuance and throughout the hold; LPs should consult their own advisor.")}</div>
          </div>
        </div>
      ),
    }),
    qsbslp: () => S("qsbslp", {
      title: "QSBS — LP economics",
      headline: H("qsbslp", `Illustrative $100,000 LP commitment: ${m.fmtX(q.wf.lpMoic)} gross MOIC becomes ${m.fmtX(q.netQ / 100000)} after tax with §1202`),
      children: (
        <>
          <table className="pb-tbl" data-testid="pb-qsbslp-tbl">
            <thead><tr><th>LP advantage</th><th className="num">Without QSBS</th><th className="num">With §1202</th><th className="num">Delta</th></tr></thead>
            <tbody>
              <tr><td>LP investment</td><td className="num">{m.fmt$(100000)}</td><td className="num">{m.fmt$(100000)}</td><td className="num">—</td></tr>
              <tr><td>Gross distributions ({m.fmtX(q.wf.lpMoic)})</td><td className="num">{m.fmt$(q.wf.lpTotal)}</td><td className="num">{m.fmt$(q.wf.lpTotal)}</td><td className="num">—</td></tr>
              <tr><td>Realised long-term gain</td><td className="num">{m.fmt$(q.gain)}</td><td className="num">{m.fmt$(q.gain)}</td><td className="num">—</td></tr>
              <tr><td>Federal tax on gain (LTCG + NIIT 23.8%)</td><td className="num">{m.fmt$(q.taxNo)}</td><td className="num">{m.fmt$(q.taxQ)}</td><td className="num pb-win">+{m.fmt$(q.saved)}</td></tr>
              <tr className="tot"><td>Net after-tax proceeds</td><td className="num">{m.fmt$(q.netNo)}</td><td className="num">{m.fmt$(q.netQ)}</td><td className="num pb-win">+{m.fmt$(q.saved)}</td></tr>
              <tr><td>Effective after-tax MOIC</td><td className="num">{m.fmtX(q.netNo / 100000)}</td><td className="num">{m.fmtX(q.netQ / 100000)}</td><td className="num pb-win">+{m.fmtX((q.netQ - q.netNo) / 100000)}</td></tr>
              <tr><td>Effective net IRR ({hold}-yr)</td><td className="num">{pctS(q.irrNo)}</td><td className="num">{pctS(q.irrQ)}</td><td className="num pb-win">+{Math.round((q.irrQ - q.irrNo) * 100)} bps</td></tr>
            </tbody>
          </table>
          <div className="pb-fine">{B("qsbslp", `Illustrative only. Actual results depend on realised MOIC, hold period, the LP's tax profile, state taxes and satisfaction of §1202 corporate-level requirements. Not tax advice.`)}</div>
        </>
      ),
    }),
    refi: () => S("refi", {
      title: "Refinance strategy",
      headline: H("refi", (thesis.refi || {}).headline || (bl && bl.active
        ? `${m.fmt$(m.loanAmt(M))} at close, incremental refinance in Year ${bl.year} — returning capital without breaking QSBS`
        : `${m.fmt$(m.loanAmt(M))} senior debt at close, amortised from operating cash across the hold`)),
      children: (
        <ol className="pb-timeline" data-testid="pb-refi-timeline">
          <li><b>Year 0{m.isRetain(M) ? "–2 · deploy" : " · close"}</b><span>Close on {M.dealName} with {m.fmt$(m.loanAmt(M))} senior debt and {m.fmt$(ask)} of LP equity.{roll ? ` Fund the committed add-ons at ${m.fmtX(rh?.blendedEntry || entry)} blended.` : ""} {m.isRetain(M) ? `${100 - Math.round(m.payoutPctOf(M))}% of free cash is retained.` : "Free cash is distributed as generated."}</span></li>
          {roll && <li><b>Years 3–{Math.max(3, hold - 1)} · self-fund</b><span>Retained cash and amortisation fund the remaining add-ons without a second LP call; the platform grows toward {m.fmt$(rh?.totEbitda || 0)} of EBITDA.</span></li>}
          {bl && bl.active && <li><b>Year {bl.year} · refinance</b><span>Draw {m.fmt$(bl.facility)} at {m.fmtX(bl.mult)} platform EBITDA (DSCR floor {m.fmtX(bl.target)}), netting {m.fmt$(bl.proceeds)} after retiring existing debt. {bl.use === "distribute" ? "All proceeds are returned to LPs as a return of capital." : `${m.fmt$(bl.toReserve)} funds further add-ons${bl.toLp > 0 ? `; ${m.fmt$(bl.toLp)} is returned to LPs` : ""}.`} Shares are not redeemed — the §1202 clock keeps running.</span></li>}
          <li><b>{M.holdIndefinite ? "Indefinite · hold" : `Year ${M.hold} · exit`}</b><span>{M.holdIndefinite ? `Hold for cash flow; distributions run at ${m.fmtX(cov.dscr)} coverage with no forced exit.` : `Sell to a strategic or larger sponsor at ${m.fmtX(exitMult)} on ${m.fmt$(ex.sdeExit)} of exit earnings. LPs realise ${m.fmtX(wf.lpMoic)} MOIC / ${pctS(wf.lpIrr)} IRR${bump ? `, ${m.fmtX(bump.afterTaxQ)} after tax with §1202` : ""}.`}</span></li>
        </ol>
      ),
    }),
    backlever: () => S("backlever", {
      title: "Back-lever scenario",
      headline: H("backlever", `Optional: refinance in Year ${bl?.year} to accelerate distributions or fund a larger tuck-in`),
      children: (
        <div className="pb-2col">
          <div>
            <h3 className="pb-h3">The mechanic</h3>
            <ol className="pb-levers">
              <li><b>Close &amp; build</b><span>{m.fmt$(m.loanAmt(M))} senior debt and {m.fmt$(ask)} LP equity at close, then grow EBITDA to {m.fmt$(bl?.ebitda || 0)} by Year {bl?.year}.</span></li>
              <li><b>Back-lever in Year {bl?.year}</b><span>Refinance at {m.fmtX(bl?.mult || 0)} EBITDA — a {m.fmt$(bl?.facility || 0)} facility held at a {m.fmtX(bl?.target || 0)} DSCR floor, netting {m.fmt$(bl?.proceeds || 0)}.</span></li>
              <li><b>Distribute or expand</b><span>{bl?.use === "distribute" ? "Proceeds are returned to LPs early as a return of capital — no new LP call." : `${m.fmt$(bl?.toReserve || 0)} funds more add-ons${bl?.toLp > 0 ? ` and ${m.fmt$(bl.toLp)} returns to LPs` : ""} — no new LP call either way.`}</span></li>
            </ol>
          </div>
          <div>
            <h3 className="pb-h3">Firepower comparison</h3>
            <table className="pb-tbl tight" data-testid="pb-bl-tbl">
              <thead><tr><th /><th className="num">Base case</th><th className="num">Back-lever Yr {bl?.year}</th></tr></thead>
              <tbody>
                <tr><td>LP equity called</td><td className="num">{fmtTight(m.lpAskOf({ ...M, blOn: false }))}</td><td className="num">{fmtTight(ask)}</td></tr>
                <tr><td>Additional LP calls</td><td className="num">$0</td><td className="num">$0</td></tr>
                <tr><td>Debt at close</td><td className="num">{fmtTight(m.loanAmt(M))}</td><td className="num">{fmtTight(m.loanAmt(M))}</td></tr>
                <tr><td>Refinance facility</td><td className="num">—</td><td className="num">{fmtTight(bl?.facility || 0)}</td></tr>
                <tr><td>Net proceeds</td><td className="num">—</td><td className="num">{fmtTight(bl?.proceeds || 0)}</td></tr>
                <tr><td>DSCR after refinance</td><td className="num">{m.fmtX(cov.dscr)}</td><td className="num">{m.fmtX(bl?.dscrAfter || 0)}</td></tr>
                <tr className="tot"><td>LP MOIC (pre-tax)</td><td className="num">{m.fmtX(m.lpWaterfall({ ...M, blOn: false }).lpMoic)}</td><td className="num">{m.fmtX(wf.lpMoic)}</td></tr>
              </tbody>
            </table>
            <div className="pb-sowhat"><span>Guardrails</span><p>{B("backlever", `Total leverage is capped at ${m.fmtX(bl?.mult || 0)} platform EBITDA and the DSCR floor of ${m.fmtX(bl?.target || 0)} is tested before the facility is drawn.`)}</p></div>
          </div>
        </div>
      ),
    }),
    scenarios: () => S("scenarios", {
      title: "Pro forma — return scenarios",
      headline: H("scenarios", `Base case ${m.fmtX(wf.lpMoic)} LP MOIC / ${pctS(wf.lpIrr)} IRR on ${m.fmt$(ask)}${bump ? ` — §1202 adds +${m.fmtX(bump.x)} after tax` : ""}`),
      children: (
        <>
          <table className="pb-tbl" data-testid="pb-scen-tbl">
            <thead><tr><th /><th className="num">Downside</th><th className="num">Base</th><th className="num">Upside</th></tr></thead>
            <tbody>
              {[
                ["LP equity called", (r) => fmtTight(r.lp)],
                ["Senior debt at close", (r) => fmtTight(r.debt)],
                ["Pro-forma EBITDA", (r) => fmtTight(r.ebitda)],
                ["Average entry multiple", (r) => m.fmtX(r.entry)],
                ["Exit multiple", (r) => m.fmtX(r.exitMult)],
                ["Enterprise value at exit", (r) => (M.holdIndefinite ? "Hold" : fmtTight(r.ev))],
                [`DSCR (Yr ${cov.year})`, (r) => m.fmtX(r.dscr)],
                ["LP MOIC (pre-tax)", (r) => m.fmtX(r.moic)],
                ["LP IRR (pre-tax)", (r) => pctS(r.irr)],
              ].map(([label, f], i) => (
                <tr key={label} className={i >= 7 ? "tot" : ""}>
                  <td>{label}</td>
                  {scens.map(([name, r]) => <td className="num" key={name}>{f(r)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pb-fine">{B("scenarios", `Downside flexes growth to ${M.downGrowth == null ? M.sdeGrowth - 4 : M.downGrowth}% and the exit multiple down ${m.fmtX(M.downExitDelta == null ? 0.75 : M.downExitDelta)}; upside flexes growth to ${M.upGrowth == null ? M.sdeGrowth + 3 : M.upGrowth}% and the exit up ${m.fmtX(M.upExitDelta == null ? 0.75 : M.upExitDelta)}. All cases hold the same ${m.fmt$(ask)} LP equity and ${M.prefReturn}% preferred return.`)}</div>
        </>
      ),
    }),
    risks: () => S("risks", {
      title: "Risks & mitigants",
      headline: H("risks", `${risks.length} real risks — and how the structure answers each`),
      children: <div className="pb-risks" data-testid="pb-risks">{risks.slice(0, 6).map((r, i) => <div key={i}><b>{r.title}</b><span>{r.text}</span></div>)}</div>,
    }),
    ask: () => S("ask", {
      title: "The ask",
      headline: H("ask", `Reserve ${m.fmt$(ask)} by close`),
      children: (
        <div className="pb-2col">
          <div className="pb-stats">
            <div><b>{m.fmtX(wf.lpMoic)}</b><span>{`Target LP MOIC over ${M.holdIndefinite ? "the hold" : `${M.hold} years`} — ${pctS(wf.lpIrr)} IRR, gross of tax`}</span></div>
            {bump && <div><b>{`+${m.fmtX(bump.x)}`}</b><span>QSBS §1202 lift after tax; federal gain excluded up to $15,000,000 or 10× basis</span></div>}
            <div><b>{m.fmtX(cov.dscr)}</b><span>{`Year-${cov.year} DSCR${cov.pf ? " fully deployed" : ""} — the ${M.prefReturn}% pref is covered from operating cash`}</span></div>
          </div>
          <div className="pb-prose">
            <h3>Next steps</h3>
            <ol className="pb-list num">
              <li>Data-room access and LP subscription documents.</li>
              <li>Diligence call with the operating team{thesis.qsbsEligible !== false ? " and a §1202 review with tax counsel" : ""}.</li>
              <li>Soft-circle a commitment by month-end; hard close within 60 days.</li>
            </ol>
            <div className="pb-fine">{B("ask", `Minimum commitment ${m.fmt$((share && share.minCommit) || 25000)} · ${M.prefReturn}% cumulative preferred return · ${M.lpSplit}/${100 - M.lpSplit} split after pref and return of capital.`)}</div>
          </div>
        </div>
      ),
    }),
  };

  return (
    <div className="pb-deck" ref={ref} data-testid="pitchbook">
      {live.map((s) => (content[s.id] ? content[s.id]() : null))}
    </div>
  );
});
